import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { formatDateTimeID } from "@/lib/format";

export default function SettingsPage() {
  const { user } = useAuth();
  const canManage = user?.role === "super_admin";
  const [company, setCompany] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setCompany(data)).catch(() => {});
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {});
    if (canManage) api.get("/sessions").then(({ data }) => setSessions(data)).catch(() => {});
  }, [canManage]);

  const saveCompany = async () => {
    try {
      const { data } = await api.put("/settings/company", company);
      setCompany(data);
      toast.success("Pengaturan perusahaan berhasil disimpan.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const terminate = async (id) => {
    try {
      await api.post(`/sessions/${id}/terminate`);
      setSessions((s) => s.filter((x) => x.id !== id));
      toast.success("Sesi pengguna berhasil diakhiri.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pengaturan</h1>
        <p className="text-slate-500 mt-2">Konfigurasi perusahaan, departemen, dan sesi pengguna.</p>
      </div>

      <Tabs defaultValue="company" className="w-full">
        <TabsList className="bg-white border border-slate-200 h-auto p-1">
          <TabsTrigger value="company" data-testid="tab-company">Profil Perusahaan</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Departemen</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Sesi Pengguna</TabsTrigger>
          <TabsTrigger value="about" data-testid="tab-about">Tentang Aplikasi</TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="font-display">Profil Perusahaan</CardTitle></CardHeader>
            <CardContent>
              {!company ? <div className="text-sm text-slate-500">Memuat data...</div> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Nama Perusahaan</Label><Input value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="company-name" /></div>
                  <div className="space-y-1.5"><Label>Nama Singkat</Label><Input value={company.short_name || ""} onChange={(e) => setCompany({ ...company, short_name: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>NPWP</Label><Input value={company.npwp || ""} onChange={(e) => setCompany({ ...company, npwp: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Nomor Telepon</Label><Input value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} /></div>
                  <div className="space-y-1.5 md:col-span-2"><Label>Alamat</Label><Input value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Provinsi</Label><Input value={company.province || ""} onChange={(e) => setCompany({ ...company, province: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Kota/Kabupaten</Label><Input value={company.city || ""} onChange={(e) => setCompany({ ...company, city: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Kode Pos</Label><Input value={company.postal_code || ""} onChange={(e) => setCompany({ ...company, postal_code: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={company.email || ""} onChange={(e) => setCompany({ ...company, email: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Situs Web</Label><Input value={company.website || ""} onChange={(e) => setCompany({ ...company, website: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Mata Uang Utama</Label><Input value={company.currency || ""} onChange={(e) => setCompany({ ...company, currency: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Awal Tahun Buku</Label><Input value={company.fiscal_year_start || ""} onChange={(e) => setCompany({ ...company, fiscal_year_start: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Zona Waktu</Label><Input value={company.timezone || ""} onChange={(e) => setCompany({ ...company, timezone: e.target.value })} /></div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button onClick={saveCompany} disabled={!canManage} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-company-button">
                      Simpan Perubahan
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="departments" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="font-display">Departemen</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {departments.map((d) => (
                  <div key={d.code} className="border border-slate-200 bg-white rounded-md p-3">
                    <div className="font-medium">{d.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Kode: <span className="font-mono-num">{d.code}</span></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sessions" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="font-display">Sesi Pengguna</CardTitle></CardHeader>
            <CardContent>
              {!canManage ? (
                <div className="text-sm text-slate-500">Anda tidak memiliki kewenangan untuk melihat sesi pengguna.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Pengguna</TableHead>
                        <TableHead>Waktu Masuk</TableHead>
                        <TableHead>Alamat IP</TableHead>
                        <TableHead>Perangkat</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Tindakan</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.length === 0 ? (
                        <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Belum ada sesi aktif.</TableCell></TableRow>
                      ) : sessions.map((s) => (
                        <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                          <TableCell>
                            <div className="font-medium">{s.user_name}</div>
                            <div className="text-xs text-slate-500">{s.user_email}</div>
                          </TableCell>
                          <TableCell className="text-xs">{formatDateTimeID(s.login_at)}</TableCell>
                          <TableCell className="text-xs font-mono-num">{s.ip || "-"}</TableCell>
                          <TableCell className="text-xs truncate max-w-[220px]">{s.device || "-"}</TableCell>
                          <TableCell><Badge className="bg-emerald-600 hover:bg-emerald-600">Aktif</Badge></TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" onClick={() => terminate(s.id)} data-testid={`terminate-session-${s.id}`}>
                              Akhiri Sesi
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="about" className="mt-4">
          <Card>
            <CardHeader><CardTitle className="font-display">Tentang Aplikasi</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div><span className="text-slate-500">Nama Aplikasi:</span> <span className="font-semibold">ABS Finance, Accounting &amp; Tax</span></div>
              <div><span className="text-slate-500">Perusahaan:</span> PT Amaydelli Berkah Sejahtera</div>
              <div><span className="text-slate-500">Versi:</span> <span className="font-mono-num">1.0.0 (Tahap 1)</span></div>
              <div className="pt-3 text-xs text-slate-500">Modul yang tersedia pada tahap ini: Dasbor, Manajemen Pengguna, Jejak Audit, Pengaturan, Profil Saya. Modul keuangan lainnya akan tersedia pada tahap pengembangan berikutnya.</div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
